import { useRef, useMemo, useEffect, useState } from 'react'
import { useFrame, useThree, useLoader } from '@react-three/fiber'
import { Sphere, Box, Cone, Cylinder, Stars, Float, Trail } from '@react-three/drei'
import * as THREE from 'three'
import { GameState } from '../types/game'

// 生き物の種類
type CreatureType = 'normal' | 'mutant_6legs' | 'flying_fish' | 'rock_beast' | 'plant_human' | 'tentacle_bird' | 'crystal_spider'

interface Creature {
  id: string
  type: CreatureType
  position: [number, number, number]
  rotation: number
  scale: number
  color: string
  animationOffset: number
}

interface Earth3DProps {
  gameState: GameState | null
}

export function Earth3D({ gameState }: Earth3DProps) {
  const earthRef = useRef<THREE.Mesh>(null)
  const cloudsRef = useRef<THREE.Mesh>(null)
  const [creatures, setCreatures] = useState<Creature[]>([])
  const [explosions, setExplosions] = useState<{id: string, position: [number, number, number], type: string}[]>([])
  const [rainDrops, setRainDrops] = useState<{id: string, position: [number, number, number], velocity: number}[]>([])
  const [stormClouds, setStormClouds] = useState<{id: string, position: [number, number, number], intensity: number}[]>([])
  const [alienShips, setAlienShips] = useState<{id: string, position: [number, number, number], type: 'mothership' | 'fighter', rotation: number}[]>([])
  const [meteorites, setMeteorites] = useState<{id: string, position: [number, number, number], velocity: [number, number, number], size: number}[]>([])
  const [laserBeams, setLaserBeams] = useState<{id: string, start: [number, number, number], end: [number, number, number]}[]>([])
  const [explosionEffects, setExplosionEffects] = useState<{id: string, position: [number, number, number], scale: number, type: 'meteor' | 'laser'}[]>([])
  const [humans, setHumans] = useState<{id: string, position: [number, number, number], type: 'individual' | 'city' | 'ship', size: number}[]>([])
  const [oceanGlow, setOceanGlow] = useState(0)
  const [volcanoEruption, setVolcanoEruption] = useState(false)
  
  // 地球のテクスチャを読み込み
  const earthTexture = useLoader(THREE.TextureLoader, '/textures/earth_daymap.jpg')
  const cloudsTexture = useLoader(THREE.TextureLoader, '/textures/earth_clouds.jpg')
  const specularTexture = useLoader(THREE.TextureLoader, '/textures/earth_specular.jpg')
  
  // テクスチャの設定を調整
  useEffect(() => {
    if (earthTexture) {
      earthTexture.anisotropy = 16
    }
    if (cloudsTexture) {
      cloudsTexture.anisotropy = 16
    }
    if (specularTexture) {
      specularTexture.anisotropy = 16
    }
  }, [earthTexture, cloudsTexture, specularTexture])
  
  // 生き物の生成ロジック
  useEffect(() => {
    if (!gameState) return
    
    const params = gameState.parameters
    const newCreatures: Creature[] = []
    
    // パラメータの極端な組み合わせで変異を決定
    const determineMutation = (): CreatureType => {
      // 放射線が高い
      if (params.atmosphere.radiation > 80) {
        if (Math.random() < 0.7) return 'mutant_6legs'
        if (Math.random() < 0.5) return 'tentacle_bird'
      }
      
      // 高温＋高湿度
      if (params.atmosphere.temperature > 60 && params.atmosphere.humidity > 70) {
        return 'flying_fish'
      }
      
      // 火山活動が極度に活発
      if (params.geology.volcanicActivity > 85) {
        if (Math.random() < 0.6) return 'rock_beast'
        return 'crystal_spider'
      }
      
      // 高度な光合成＋文明
      if (params.biosphere.photosynthesisRate > 70 && params.civilization.technologyLevel > 40) {
        return 'plant_human'
      }
      
      // 生態系崩壊
      if (params.biosphere.biodiversity < 20 && params.atmosphere.toxicity > 60) {
        return Math.random() < 0.5 ? 'tentacle_bird' : 'crystal_spider'
      }
      
      return 'normal'
    }
    
    // 生物多様性に基づいて生き物を配置
    const creatureCount = Math.max(0, Math.min(30, Math.floor(params.biosphere.biodiversity / 5)))
    
    for (let i = 0; i < creatureCount; i++) {
      const angle = (i / creatureCount) * Math.PI * 2 + Math.random() * 0.5
      const latitude = (Math.random() - 0.5) * Math.PI
      
      const type = determineMutation()
      
      // タイプごとの色設定
      const colors: Record<CreatureType, string> = {
        normal: `hsl(${120 + Math.random() * 60}, 70%, 50%)`,
        mutant_6legs: '#FF00FF',
        flying_fish: '#00DDFF',
        rock_beast: '#8B4513',
        plant_human: '#32CD32',
        tentacle_bird: '#FF6B6B',
        crystal_spider: '#E0B0FF'
      }
      
      const radius = 2.52 + Math.random() * 0.05
      newCreatures.push({
        id: `creature-${i}-${Date.now()}`,
        type,
        position: [
          Math.cos(angle) * Math.cos(latitude) * radius,
          Math.sin(latitude) * radius,
          Math.sin(angle) * Math.cos(latitude) * radius
        ],
        rotation: Math.random() * Math.PI * 2,
        scale: 0.03 + Math.random() * 0.04,
        color: colors[type],
        animationOffset: Math.random() * Math.PI * 2
      })
    }
    
    setCreatures(newCreatures)
    
    // 雨エフェクトの管理 - 必ず表示されるよう調整
    const rainCount = 50 // 固定で50個表示
    const newRainDrops = Array.from({ length: rainCount }, (_, i) => ({
      id: `rain-${i}-${Date.now()}`,
      position: [
        (Math.random() - 0.5) * 10,
        4 + Math.random() * 3,
        (Math.random() - 0.5) * 10
      ] as [number, number, number],
      velocity: 0.08 + Math.random() * 0.12
    }))
    setRainDrops(newRainDrops)
    
    // 嵐エフェクトの管理 - 必ず表示されるよう調整  
    const stormCount = 8 // 固定で8個表示
    const newStormClouds = Array.from({ length: stormCount }, (_, i) => ({
      id: `storm-${i}-${Date.now()}`,
      position: [
        Math.cos(i * Math.PI * 0.25) * (3 + Math.random() * 2),
        1 + Math.random() * 2,
        Math.sin(i * Math.PI * 0.25) * (3 + Math.random() * 2)
      ] as [number, number, number],
      intensity: 0.7 + Math.random() * 0.3
    }))
    setStormClouds(newStormClouds)
    
    // 宇宙侵略イベント - 必ず表示されるよう調整
    if (gameState.parameters.civilization.technologyLevel >= 0) { // 常に表示
      const shipCount = 5 // 固定で5隻表示
      const newAlienShips = Array.from({ length: shipCount }, (_, i) => ({
        id: `alien-${i}-${Date.now()}`,
        position: [
          Math.cos(i * Math.PI * 0.4) * (8 + Math.random() * 4),
          2 + Math.random() * 3,
          Math.sin(i * Math.PI * 0.4) * (8 + Math.random() * 4)
        ] as [number, number, number],
        type: (i === 0 ? 'mothership' : 'fighter') as 'mothership' | 'fighter',
        rotation: Math.random() * Math.PI * 2
      }))
      setAlienShips(newAlienShips)
      
      // レーザービーム攻撃
      const beamCount = Math.floor(shipCount / 2)
      const newLaserBeams = Array.from({ length: beamCount }, (_, i) => ({
        id: `laser-${i}-${Date.now()}`,
        start: newAlienShips[i + 1]?.position || [0, 5, 0],
        end: [
          (Math.random() - 0.5) * 5,
          2.5,
          (Math.random() - 0.5) * 5
        ] as [number, number, number]
      }))
      setLaserBeams(newLaserBeams)
    }
    
    // 隕石衝突イベント - 必ず表示されるよう調整
    if (gameState.parameters.geology.seismicActivity >= 0) { // 常に表示
      const meteorCount = 6 // 固定で6個表示
      const newMeteorites = Array.from({ length: meteorCount }, (_, i) => ({
        id: `meteor-${i}-${Date.now()}`,
        position: [
          (Math.random() - 0.5) * 20,
          8 + Math.random() * 5,
          (Math.random() - 0.5) * 20
        ] as [number, number, number],
        velocity: [
          (Math.random() - 0.5) * 0.2,
          -0.3 - Math.random() * 0.2,
          (Math.random() - 0.5) * 0.2
        ] as [number, number, number],
        size: 0.3 + Math.random() * 0.7
      }))
      setMeteorites(newMeteorites)
    }
    
    // 人口に応じて人々を表示
    const population = gameState.parameters.civilization.population
    if (population > 0) {
      const humanDisplays = []
      
      // 陸地を優先した配置関数
      const isLandArea = (lat: number, lng: number) => {
        // 簡単な陸地判定（実際の地球の大陸位置に近似）
        const absLat = Math.abs(lat)
        const normLng = ((lng % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI)
        
        // 主要大陸エリア
        const continentAreas = [
          // アジア・ヨーロッパ
          { latMin: 0.2, latMax: 1.3, lngMin: 0, lngMax: 2.5 },
          // 北米
          { latMin: 0.3, latMax: 1.2, lngMin: 4.0, lngMax: 5.5 },
          // 南米
          { latMin: -1.0, latMax: 0.2, lngMin: 4.5, lngMax: 5.2 },
          // アフリカ
          { latMin: -0.6, latMax: 0.6, lngMin: 0.8, lngMax: 1.8 },
          // オーストラリア
          { latMin: -0.6, latMax: -0.2, lngMin: 2.3, lngMax: 2.8 }
        ]
        
        return continentAreas.some(area => 
          lat >= area.latMin && lat <= area.latMax && 
          normLng >= area.lngMin && normLng <= area.lngMax
        )
      }
      
      // 個人レベル（人口が少ない時）
      if (population < 50000) {
        const individualCount = Math.min(100, Math.floor(population / 500))
        let landCount = 0
        let seaCount = 0
        
        for (let i = 0; i < individualCount; i++) {
          let angle, latitude, isOnLand
          let attempts = 0
          
          // 陸地を優先して配置（80%陸地、20%海）
          do {
            angle = Math.random() * Math.PI * 2
            latitude = (Math.random() - 0.5) * Math.PI * 0.8
            isOnLand = isLandArea(latitude, angle)
            attempts++
          } while (attempts < 10 && ((landCount / (landCount + seaCount + 1)) < 0.8) && !isOnLand)
          
          if (isOnLand || landCount / (landCount + seaCount + 1) >= 0.8) {
            landCount++
          } else {
            seaCount++
          }
          
          const radius = 2.51
          humanDisplays.push({
            id: `human-${i}-${Date.now()}`,
            position: [
              Math.cos(angle) * Math.cos(latitude) * radius,
              Math.sin(latitude) * radius,
              Math.sin(angle) * Math.cos(latitude) * radius
            ] as [number, number, number],
            type: isOnLand ? 'individual' : 'ship' as 'individual' | 'ship',
            size: 0.02
          })
        }
      }
      
      // 都市レベル（人口が多い時）
      if (population >= 10000) {
        const cityCount = Math.min(20, Math.floor(population / 10000))
        for (let i = 0; i < cityCount; i++) {
          let angle, latitude, isOnLand
          let attempts = 0
          
          // 都市は必ず陸地に配置
          do {
            angle = Math.random() * Math.PI * 2
            latitude = (Math.random() - 0.5) * Math.PI * 0.6
            isOnLand = isLandArea(latitude, angle)
            attempts++
          } while (attempts < 20 && !isOnLand)
          
          const radius = 2.52
          const citySize = Math.min(0.3, 0.05 + (population / 100000) * 0.1)
          humanDisplays.push({
            id: `city-${i}-${Date.now()}`,
            position: [
              Math.cos(angle) * Math.cos(latitude) * radius,
              Math.sin(latitude) * radius,
              Math.sin(angle) * Math.cos(latitude) * radius
            ] as [number, number, number],
            type: 'city' as const,
            size: citySize
          })
        }
      }
      
      setHumans(humanDisplays)
    } else {
      setHumans([])
    }
  }, [gameState])
  
  // アニメーション
  useFrame((state, delta) => {
    if (earthRef.current) {
      earthRef.current.rotation.y += delta * 0.05
      
      // 地震エフェクト
      if (gameState?.parameters.geology.seismicActivity > 70) {
        earthRef.current.position.x = Math.sin(state.clock.elapsedTime * 20) * 0.02
        earthRef.current.position.y = Math.cos(state.clock.elapsedTime * 15) * 0.02
      } else {
        earthRef.current.position.x = 0
        earthRef.current.position.y = 0
      }
    }
    if (cloudsRef.current) {
      cloudsRef.current.rotation.y += delta * 0.08
      cloudsRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.1) * 0.05
      
      // 嵐エフェクト
      if (gameState?.parameters.atmosphere.humidity > 80) {
        cloudsRef.current.rotation.y += delta * 0.5
      }
    }
  })
  
  // 生き物コンポーネント
  const CreatureModel = ({ creature, time }: { creature: Creature; time: number }) => {
    const groupRef = useRef<THREE.Group>(null)
    
    useFrame((state) => {
      if (groupRef.current) {
        const t = state.clock.elapsedTime + creature.animationOffset
        
        // 浮遊アニメーション
        if (creature.type === 'flying_fish' || creature.type === 'tentacle_bird') {
          groupRef.current.position.y = creature.position[1] + Math.sin(t * 2) * 0.1
        }
        
        // 回転アニメーション
        if (creature.type === 'crystal_spider') {
          groupRef.current.rotation.y = t * 0.5
        }
      }
    })
    
    const baseProps = {
      ref: groupRef,
      position: creature.position,
      rotation: [0, creature.rotation, 0] as [number, number, number],
      scale: creature.scale
    }
    
    switch (creature.type) {
      case 'mutant_6legs':
        return (
          <group {...baseProps}>
            <Box args={[0.3, 0.5, 0.2]}>
              <meshStandardMaterial color={creature.color} emissive={creature.color} emissiveIntensity={0.2} />
            </Box>
            <Sphere args={[0.15]} position={[0, 0.35, 0]}>
              <meshStandardMaterial color={creature.color} />
            </Sphere>
            {[0, 60, 120, 180, 240, 300].map((angle, i) => (
              <Cylinder
                key={i}
                args={[0.03, 0.03, 0.3]}
                position={[
                  Math.cos(angle * Math.PI / 180) * 0.2,
                  -0.25,
                  Math.sin(angle * Math.PI / 180) * 0.2
                ]}
                rotation={[Math.sin(time + i) * 0.2, 0, 0]}
              >
                <meshStandardMaterial color={creature.color} />
              </Cylinder>
            ))}
          </group>
        )
      
      case 'flying_fish':
        return (
          <group {...baseProps}>
            <Cone args={[0.1, 0.4, 8]} rotation={[0, 0, Math.PI / 2]}>
              <meshStandardMaterial color={creature.color} metalness={0.8} roughness={0.2} />
            </Cone>
            <Box args={[0.6, 0.02, 0.3]} position={[0, 0.1, 0]}>
              <meshStandardMaterial color={creature.color} transparent opacity={0.6} />
            </Box>
          </group>
        )
      
      case 'rock_beast':
        return (
          <group {...baseProps}>
            <Box args={[0.4, 0.3, 0.3]}>
              <meshStandardMaterial color={creature.color} roughness={1} metalness={0} />
            </Box>
            {[-0.15, 0, 0.15].map((x, i) => (
              <Cone key={i} args={[0.05, 0.25, 4]} position={[x, 0.25, 0]}>
                <meshStandardMaterial 
                  color="#FF4500" 
                  emissive="#FF0000" 
                  emissiveIntensity={0.8}
                />
              </Cone>
            ))}
          </group>
        )
      
      case 'plant_human':
        return (
          <group {...baseProps}>
            <Cylinder args={[0.08, 0.12, 0.5]}>
              <meshStandardMaterial color="#8B4513" />
            </Cylinder>
            <Sphere args={[0.25]} position={[0, 0.35, 0]}>
              <meshStandardMaterial color={creature.color} emissive="#00FF00" emissiveIntensity={0.1} />
            </Sphere>
            {[-0.2, 0.2].map((x, i) => (
              <Cylinder 
                key={i}
                args={[0.03, 0.03, 0.3]} 
                position={[x, 0.1, 0]} 
                rotation={[0, 0, x > 0 ? -Math.PI / 4 : Math.PI / 4]}
              >
                <meshStandardMaterial color="#654321" />
              </Cylinder>
            ))}
          </group>
        )
      
      case 'tentacle_bird':
        return (
          <group {...baseProps}>
            <Sphere args={[0.2]}>
              <meshStandardMaterial color={creature.color} />
            </Sphere>
            {[0, 72, 144, 216, 288].map((angle, i) => (
              <Cylinder
                key={i}
                args={[0.02, 0.01, 0.4]}
                position={[
                  Math.cos(angle * Math.PI / 180) * 0.15,
                  -0.2,
                  Math.sin(angle * Math.PI / 180) * 0.15
                ]}
                rotation={[Math.sin(time * 2 + i) * 0.3, 0, 0]}
              >
                <meshStandardMaterial color={creature.color} transparent opacity={0.8} />
              </Cylinder>
            ))}
          </group>
        )
      
      case 'crystal_spider':
        return (
          <group {...baseProps}>
            <Box args={[0.2, 0.1, 0.2]}>
              <meshStandardMaterial 
                color={creature.color} 
                metalness={0.9} 
                roughness={0.1}
                emissive={creature.color}
                emissiveIntensity={0.3}
              />
            </Box>
            {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
              <Box
                key={i}
                args={[0.3, 0.02, 0.02]}
                position={[
                  Math.cos(angle * Math.PI / 180) * 0.15,
                  0,
                  Math.sin(angle * Math.PI / 180) * 0.15
                ]}
                rotation={[0, angle * Math.PI / 180, Math.sin(time + i) * 0.1]}
              >
                <meshStandardMaterial 
                  color={creature.color} 
                  metalness={1} 
                  roughness={0}
                />
              </Box>
            ))}
          </group>
        )
      
      default:
        return (
          <Sphere {...baseProps} args={[0.2]}>
            <meshStandardMaterial color={creature.color} />
          </Sphere>
        )
    }
  }
  
  // パラメータに基づく雲の設定
  const cloudOpacity = useMemo(() => {
    if (!gameState) return 0.4
    return Math.min(0.8, gameState.parameters.atmosphere.humidity / 100)
  }, [gameState])
  
  return (
    <>
      <Stars 
        radius={100} 
        depth={50} 
        count={5000} 
        factor={4} 
        saturation={0} 
        fade 
      />
      
      <group>
        {/* 地球本体（ディスプレースメントマップで凹凸を追加） */}
        <Float
          speed={gameState?.parameters.geology.seismicActivity > 50 ? 10 : 1}
          rotationIntensity={gameState?.parameters.geology.seismicActivity > 50 ? 0.5 : 0.1}
          floatIntensity={gameState?.parameters.geology.seismicActivity > 50 ? 0.3 : 0.05}
        >
          <Sphere ref={earthRef} args={[2.5, 256, 256]}>
            <meshPhongMaterial 
              map={earthTexture}
              specularMap={specularTexture}
              specular={new THREE.Color('grey')}
              shininess={10}
              emissive={
                gameState?.parameters.geology.volcanicActivity > 90 ? '#FF0000' :
                gameState?.parameters.geology.volcanicActivity > 70 ? '#FF3300' : 
                '#000000'
              }
              emissiveIntensity={
                gameState?.parameters.geology.volcanicActivity > 90 ? 0.5 :
                gameState?.parameters.geology.volcanicActivity > 70 ? 0.2 : 
                0
              }
            />
          </Sphere>
        </Float>
        
        {/* 雲層 - 常に表示 */}
        <Sphere ref={cloudsRef} args={[2.53, 64, 64]}>
          <meshPhongMaterial 
            map={cloudsTexture}
            transparent
            opacity={gameState ? Math.max(0.4, cloudOpacity * 1.2) : 0.5}
            depthWrite={false}
            emissive={gameState?.parameters.atmosphere.radiation > 60 ? '#00FF00' : '#000000'}
            emissiveIntensity={gameState?.parameters.atmosphere.radiation > 60 ? 0.3 : 0}
          />
        </Sphere>
        
        {/* 大気の輝き */}
        <Sphere args={[2.6, 32, 32]}>
          <meshBasicMaterial 
            color={gameState ? 
              `hsl(${200 - gameState.parameters.atmosphere.toxicity}, 80%, 50%)` : 
              '#4A90E2'
            }
            transparent
            opacity={gameState?.parameters.atmosphere.toxicity > 70 ? 0.4 : 0.15}
            side={THREE.BackSide}
            depthWrite={false}
          />
        </Sphere>
        
        {/* 火山噴火エフェクト - 常にアクティブレベルで表示 */}
        {gameState && (
          <>
            {/* 溶岩の噴出 */}
            {[...Array(
              Math.max(5, Math.floor(gameState.parameters.geology.volcanicActivity / 10))
            )].map((_, i, arr) => {
              const angle = (i / arr.length) * Math.PI * 2
              const radius = 2.5 + Math.sin(i * 0.8) * 0.1
              const intensity = gameState.parameters.geology.volcanicActivity / 100
              return (
                <group key={`volcano-${i}`} position={[
                  Math.cos(angle) * radius,
                  Math.sin(i * Math.PI * 0.2) * 0.5 * intensity,
                  Math.sin(angle) * radius
                ]}>
                  {/* 溶岩の軌跡 */}
                  <Trail
                    width={1.5 * intensity}
                    length={20 + intensity * 10}
                    color={intensity > 0.8 ? '#FFFFFF' : '#FF4500'}
                    attenuation={(t) => t * t}
                  >
                    <Float speed={8 * intensity} floatIntensity={3 * intensity} rotationIntensity={2}>
                      <Sphere args={[0.15 * (1 + intensity)]}>
                        <meshBasicMaterial 
                          color={intensity > 0.9 ? "#FFFF00" : "#FFAA00"} 
                          emissive={intensity > 0.8 ? "#FF0000" : "#000000"}
                          emissiveIntensity={intensity * 2}
                        />
                      </Sphere>
                    </Float>
                  </Trail>
                  
                  {/* 火花エフェクト */}
                  {[...Array(Math.floor(3 * intensity) + 2)].map((_, j) => (
                    <Float key={j} speed={12 + j * 2 + intensity * 5} floatIntensity={4 * intensity}>
                      <Sphere args={[0.05]} position={[
                        (Math.random() - 0.5) * 0.6 * intensity,
                        Math.random() * 0.3 * intensity,
                        (Math.random() - 0.5) * 0.6 * intensity
                      ]}>
                        <meshBasicMaterial 
                          color={intensity > 0.8 ? "#FFFFFF" : "#FFF700"} 
                          emissive={intensity > 0.7 ? "#FF4500" : "#000000"}
                          emissiveIntensity={intensity}
                        />
                      </Sphere>
                    </Float>
                  ))}
                </group>
              )
            })}
            
            {/* 噴煙エフェクト */}
            {[...Array(8)].map((_, i) => (
              <Float key={`smoke-${i}`} speed={2} floatIntensity={1}>
                <Sphere 
                  args={[0.3 + Math.random() * 0.2, 8, 8]} 
                  position={[
                    Math.cos(i * Math.PI * 0.25) * 2.6,
                    2 + i * 0.3,
                    Math.sin(i * Math.PI * 0.25) * 2.6
                  ]}
                >
                  <meshBasicMaterial 
                    color="#333333" 
                    transparent 
                    opacity={0.6 - i * 0.05} 
                  />
                </Sphere>
              </Float>
            ))}
            
            {/* 地面の亀裂から光が漏れる */}
            {[...Array(6)].map((_, i) => {
              const crackAngle = (i / 6) * Math.PI * 2
              return (
                <Box 
                  key={`crack-${i}`}
                  args={[0.02, 0.5, 0.8]}
                  position={[
                    Math.cos(crackAngle) * 2.5,
                    -0.1,
                    Math.sin(crackAngle) * 2.5
                  ]}
                  rotation={[0, crackAngle, 0]}
                >
                  <meshBasicMaterial 
                    color="#FF3300" 
                    emissive="#FF0000"
                    emissiveIntensity={2}
                  />
                </Box>
              )
            })}
          </>
        )}
        
        {/* 氷河エフェクト - アクティブレベルで表示 */}
        {gameState?.parameters.hydrosphere.iceCoverage > 30 && (
          <Sphere args={[2.52, 32, 32]}>
            <meshStandardMaterial
              color="#E0F7FF"
              transparent
              opacity={0.6}
              metalness={0.9}
              roughness={0.1}
              emissive="#00CCFF"
              emissiveIntensity={0.1}
            />
          </Sphere>
        )}
        
        {/* 汚染エフェクト - アクティブレベルで表示 */}
        {gameState?.parameters.civilization.pollution > 30 && (
          <>
            {[...Array(20)].map((_, i) => {
              const angle = (i / 20) * Math.PI * 2
              const height = Math.random() * 2 - 1
              return (
                <Float key={`pollution-${i}`} speed={2} floatIntensity={0.5}>
                  <Box 
                    position={[
                      Math.cos(angle) * 2.7,
                      height,
                      Math.sin(angle) * 2.7
                    ]}
                    args={[0.05, 0.05, 0.05]}
                  >
                    <meshBasicMaterial color="#333333" opacity={0.7} transparent />
                  </Box>
                </Float>
              )
            })}
          </>
        )}
        
        {/* 雨エフェクト - 常に表示 */}
        {rainDrops.map(drop => (
          <group key={drop.id}>
            <Trail
              width={0.5}
              length={10}
              color="#87CEEB"
              attenuation={(t) => t * t * t}
            >
              <Float speed={8} floatIntensity={0.2} rotationIntensity={0}>
                <Cylinder 
                  args={[0.005, 0.008, 0.3]} 
                  position={drop.position}
                  rotation={[0, 0, Math.random() * 0.2]}
                >
                  <meshBasicMaterial 
                    color="#87CEEB" 
                    transparent 
                    opacity={0.8}
                  />
                </Cylinder>
              </Float>
            </Trail>
          </group>
        ))}
        
        {/* 嵐エフェクト - 常に表示 */}
        {stormClouds.map(storm => (
          <group key={storm.id} position={storm.position}>
            {/* 暗い嵐雲 */}
            <Float speed={4 * storm.intensity} floatIntensity={2 * storm.intensity}>
              <Sphere args={[1.2 * storm.intensity, 16, 16]}>
                <meshBasicMaterial 
                  color="#2C2C2C" 
                  transparent 
                  opacity={0.8 * storm.intensity}
                />
              </Sphere>
            </Float>
            
            {/* 稲妻エフェクト */}
            {[...Array(Math.floor(3 * storm.intensity))].map((_, i) => (
              <Float key={i} speed={15 + i * 3} floatIntensity={4 * storm.intensity}>
                <Box 
                  args={[0.05, 2 * storm.intensity, 0.05]} 
                  position={[
                    (Math.random() - 0.5) * 2 * storm.intensity,
                    -storm.intensity,
                    (Math.random() - 0.5) * 2 * storm.intensity
                  ]}
                  rotation={[
                    Math.random() * Math.PI * 0.2,
                    Math.random() * Math.PI * 2,
                    Math.random() * Math.PI * 0.2
                  ]}
                >
                  <meshBasicMaterial 
                    color="#FFFFFF" 
                    emissive="#FFFF00"
                    emissiveIntensity={storm.intensity * 2}
                  />
                </Box>
              </Float>
            ))}
            
            {/* 雨の軌跡 */}
            {[...Array(Math.floor(20 * storm.intensity))].map((_, i) => (
              <Trail
                key={i}
                width={0.3}
                length={15}
                color="#4169E1"
                attenuation={(t) => t * t}
              >
                <Float speed={12} floatIntensity={1} rotationIntensity={0}>
                  <Cylinder 
                    args={[0.003, 0.006, 0.8]} 
                    position={[
                      (Math.random() - 0.5) * 3 * storm.intensity,
                      Math.random() * 2,
                      (Math.random() - 0.5) * 3 * storm.intensity
                    ]}
                  >
                    <meshBasicMaterial 
                      color="#4169E1" 
                      transparent 
                      opacity={0.9}
                    />
                  </Cylinder>
                </Float>
              </Trail>
            ))}
          </group>
        ))}
        
        {/* 宇宙侵略エフェクト - 常に表示 */}
        {alienShips.map(ship => (
          <group key={ship.id} position={ship.position}>
            {ship.type === 'mothership' ? (
              // マザーシップ
              <group>
                <Float speed={1} floatIntensity={0.5} rotationIntensity={0.3}>
                  <Cylinder args={[1.5, 0.8, 0.3, 16]}>
                    <meshStandardMaterial 
                      color="#666666" 
                      emissive="#00FF00" 
                      emissiveIntensity={0.5}
                      metalness={0.8}
                      roughness={0.2}
                    />
                  </Cylinder>
                  {/* マザーシップの光る部分 */}
                  <Sphere args={[0.1]} position={[0, 0.2, 0]}>
                    <meshBasicMaterial color="#00FFFF" emissive="#00FFFF" emissiveIntensity={2} />
                  </Sphere>
                  {[...Array(8)].map((_, i) => (
                    <Sphere key={i} args={[0.05]} position={[
                      Math.cos(i * Math.PI * 0.25) * 1.2,
                      0.1,
                      Math.sin(i * Math.PI * 0.25) * 1.2
                    ]}>
                      <meshBasicMaterial color="#FF0000" emissive="#FF0000" emissiveIntensity={1.5} />
                    </Sphere>
                  ))}
                </Float>
              </group>
            ) : (
              // 戦闘機
              <group rotation={[0, ship.rotation, 0]}>
                <Float speed={3} floatIntensity={1} rotationIntensity={0.5}>
                  <Cone args={[0.3, 1.2, 6]}>
                    <meshStandardMaterial 
                      color="#333333" 
                      emissive="#FF4500" 
                      emissiveIntensity={0.8}
                      metalness={1}
                      roughness={0}
                    />
                  </Cone>
                  {/* エンジンの光 */}
                  <Sphere args={[0.1]} position={[0, -0.5, 0]}>
                    <meshBasicMaterial color="#0080FF" emissive="#0080FF" emissiveIntensity={3} />
                  </Sphere>
                  {/* ウィング */}
                  <Box args={[1, 0.05, 0.2]} position={[0, 0.2, 0]}>
                    <meshStandardMaterial color="#222222" metalness={0.9} roughness={0.1} />
                  </Box>
                </Float>
              </group>
            )}
          </group>
        ))}
        
        {/* レーザービーム攻撃 - 常に表示 */}
        {laserBeams.map(beam => (
          <group key={beam.id}>
            <Trail
              width={0.8}
              length={30}
              color="#FF0000"
              attenuation={(t) => 1 - t}
            >
              <Float speed={15} floatIntensity={0.5} rotationIntensity={2}>
                <Cylinder 
                  args={[0.02, 0.02, 8]} 
                  position={[
                    (beam.start[0] + beam.end[0]) / 2,
                    (beam.start[1] + beam.end[1]) / 2,
                    (beam.start[2] + beam.end[2]) / 2
                  ]}
                  rotation={[Math.PI / 2, 0, 0]}
                >
                  <meshBasicMaterial 
                    color="#FF0000" 
                    emissive="#FFFF00"
                    emissiveIntensity={3}
                  />
                </Cylinder>
              </Float>
            </Trail>
          </group>
        ))}
        
        {/* 隕石衝突エフェクト - 常に表示 */}
        {meteorites.map(meteor => (
          <group key={meteor.id} position={meteor.position}>
            <Trail
              width={meteor.size * 2}
              length={50}
              color="#FF8800"
              attenuation={(t) => t * t * t}
            >
              <Float speed={20} floatIntensity={3} rotationIntensity={5}>
                <Sphere args={[meteor.size]}>
                  <meshBasicMaterial 
                    color="#FF4500" 
                    emissive="#FFAA00"
                    emissiveIntensity={2}
                  />
                </Sphere>
                {/* 隕石の破片 */}
                {[...Array(Math.floor(meteor.size * 10))].map((_, i) => (
                  <Sphere key={i} args={[0.05]} position={[
                    (Math.random() - 0.5) * meteor.size * 3,
                    (Math.random() - 0.5) * meteor.size * 3,
                    (Math.random() - 0.5) * meteor.size * 3
                  ]}>
                    <meshBasicMaterial 
                      color="#FF6600" 
                      emissive="#FF0000"
                      emissiveIntensity={1}
                    />
                  </Sphere>
                ))}
              </Float>
            </Trail>
          </group>
        ))}
        
        {/* 爆発エフェクト */}
        {explosionEffects.map(explosion => (
          <group key={explosion.id} position={explosion.position}>
            <Float speed={25} floatIntensity={5} rotationIntensity={10}>
              <Sphere args={[explosion.scale, 16, 16]}>
                <meshBasicMaterial 
                  color={explosion.type === 'meteor' ? "#FFAA00" : "#FF00FF"}
                  emissive={explosion.type === 'meteor' ? "#FF0000" : "#FFFFFF"}
                  emissiveIntensity={4}
                  transparent
                  opacity={0.8}
                />
              </Sphere>
              {/* 爆発の火花 */}
              {[...Array(20)].map((_, i) => (
                <Float key={i} speed={30 + i * 2} floatIntensity={8}>
                  <Sphere args={[0.1]} position={[
                    (Math.random() - 0.5) * explosion.scale * 4,
                    (Math.random() - 0.5) * explosion.scale * 4,
                    (Math.random() - 0.5) * explosion.scale * 4
                  ]}>
                    <meshBasicMaterial 
                      color="#FFFFFF" 
                      emissive="#FFFF00"
                      emissiveIntensity={3}
                    />
                  </Sphere>
                </Float>
              ))}
            </Float>
          </group>
        ))}
        
        {/* 人々の表示 - 常に表示 */}
        {humans.map(human => (
          <group key={human.id} position={human.position}>
            {human.type === 'individual' ? (
              // 個人（小さな人型）
              <Float speed={2} floatIntensity={0.1} rotationIntensity={0.2}>
                <group>
                  {/* 頭 */}
                  <Sphere args={[0.008]} position={[0, 0.02, 0]}>
                    <meshStandardMaterial color="#FFDBAC" />
                  </Sphere>
                  {/* 体 */}
                  <Cylinder args={[0.005, 0.008, 0.03]} position={[0, 0, 0]}>
                    <meshStandardMaterial color="#4169E1" />
                  </Cylinder>
                  {/* 腕 */}
                  <Cylinder args={[0.002, 0.002, 0.02]} position={[-0.012, 0, 0]} rotation={[0, 0, Math.PI/4]}>
                    <meshStandardMaterial color="#FFDBAC" />
                  </Cylinder>
                  <Cylinder args={[0.002, 0.002, 0.02]} position={[0.012, 0, 0]} rotation={[0, 0, -Math.PI/4]}>
                    <meshStandardMaterial color="#FFDBAC" />
                  </Cylinder>
                  {/* 足 */}
                  <Cylinder args={[0.003, 0.003, 0.02]} position={[-0.005, -0.025, 0]}>
                    <meshStandardMaterial color="#8B4513" />
                  </Cylinder>
                  <Cylinder args={[0.003, 0.003, 0.02]} position={[0.005, -0.025, 0]}>
                    <meshStandardMaterial color="#8B4513" />
                  </Cylinder>
                </group>
              </Float>
            ) : human.type === 'ship' ? (
              // 船（海にいる人用）
              <Float speed={1} floatIntensity={0.3} rotationIntensity={0.1}>
                <group>
                  {/* 船体 */}
                  <Box args={[0.04, 0.01, 0.08]} position={[0, 0.005, 0]}>
                    <meshStandardMaterial color="#8B4513" />
                  </Box>
                  {/* マスト */}
                  <Cylinder args={[0.001, 0.001, 0.06]} position={[0, 0.04, 0]}>
                    <meshStandardMaterial color="#654321" />
                  </Cylinder>
                  {/* 帆 */}
                  <Box args={[0.03, 0.04, 0.001]} position={[0.015, 0.03, 0]}>
                    <meshStandardMaterial color="#FFFFFF" transparent opacity={0.8} />
                  </Box>
                  {/* 人（船員） */}
                  <group position={[0, 0.02, 0.01]}>
                    <Sphere args={[0.005]} position={[0, 0.01, 0]}>
                      <meshStandardMaterial color="#FFDBAC" />
                    </Sphere>
                    <Cylinder args={[0.003, 0.005, 0.02]} position={[0, 0, 0]}>
                      <meshStandardMaterial color="#FF0000" />
                    </Cylinder>
                  </group>
                  {/* 波エフェクト */}
                  <Sphere args={[0.02, 8, 4]} position={[0, -0.005, 0]}>
                    <meshBasicMaterial color="#87CEEB" transparent opacity={0.3} />
                  </Sphere>
                </group>
              </Float>
            ) : (
              // 都市（建物群）
              <group>
                {/* メインビル */}
                <Float speed={0.5} floatIntensity={0.05} rotationIntensity={0.1}>
                  <Box args={[human.size * 0.3, human.size * 2, human.size * 0.3]} position={[0, human.size, 0]}>
                    <meshStandardMaterial 
                      color="#C0C0C0" 
                      metalness={0.6} 
                      roughness={0.4}
                    />
                  </Box>
                  {/* 都市の光 */}
                  {[...Array(Math.floor(human.size * 20))].map((_, i) => (
                    <Sphere key={i} args={[0.005]} position={[
                      (Math.random() - 0.5) * human.size * 0.8,
                      Math.random() * human.size * 1.8,
                      (Math.random() - 0.5) * human.size * 0.8
                    ]}>
                      <meshBasicMaterial 
                        color="#FFFF00" 
                        emissive="#FFFF00"
                        emissiveIntensity={1.5}
                      />
                    </Sphere>
                  ))}
                  
                  {/* 周辺の建物 */}
                  {[...Array(Math.floor(human.size * 15))].map((_, i) => (
                    <Box key={i} args={[
                      0.02 + Math.random() * 0.04,
                      0.05 + Math.random() * human.size,
                      0.02 + Math.random() * 0.04
                    ]} position={[
                      (Math.random() - 0.5) * human.size * 1.5,
                      (0.05 + Math.random() * human.size) / 2,
                      (Math.random() - 0.5) * human.size * 1.5
                    ]}>
                      <meshStandardMaterial 
                        color={`hsl(${200 + Math.random() * 40}, 30%, ${40 + Math.random() * 20}%)`}
                        metalness={0.7}
                        roughness={0.3}
                      />
                    </Box>
                  ))}
                </Float>
              </group>
            )}
          </group>
        ))}
        
        {/* 生き物たち - 常に表示 */}
        {creatures.map(creature => (
          <CreatureModel 
            key={creature.id} 
            creature={creature} 
            time={0}
          />
        ))}
      </group>
      
      {/* ライティング */}
      <ambientLight intensity={gameState?.parameters.atmosphere.radiation > 50 ? 0.8 : 0.5} />
      <directionalLight 
        position={[10, 5, 5]} 
        intensity={gameState?.parameters.atmosphere.temperature > 50 ? 1.8 : 1.2}
        color={gameState?.parameters.atmosphere.temperature > 70 ? '#FFAA00' : '#FFFFFF'}
        castShadow
        shadow-mapSize={[2048, 2048]}
      />
      <pointLight 
        position={[-10, -5, -5]} 
        intensity={0.3} 
        color="#87CEEB"
      />
      
      {/* 放射線グロー */}
      {gameState?.parameters.atmosphere.radiation > 70 && (
        <pointLight
          position={[0, 0, 0]}
          intensity={2}
          color="#00FF00"
          distance={10}
        />
      )}
    </>
  )
}